// 떨어지는 물: 폭포 탕의 나무 수로, 대나무 물꼭지(가케이), 통 탕의 삼나무 물꼭지. 프로토타입 426–467.
import * as THREE from 'three';
import { mk } from '../../engine/geometry';
import { plush } from '../../engine/materials';
import { rnd } from '../../engine/math';
import { steamTex } from '../../engine/textures';
import { FRONT, type Pool } from './layout';
import { cedarDark, logWood, snowMat } from './materials';
import { WATER_Y } from './baths';
import type { QuietScene } from './scene';

const streamMat = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, transparent: true, opacity: 0.55, depthWrite: false });
const bambooPipeMat = plush('#8DA858', '#D4E2B4', 0.05);

export function buildStreams(Q: QuietScene): THREE.Mesh[] {
  const { world, scenery, L } = Q, streams: THREE.Mesh[] = [];
  /** 물줄기 원기둥과 수면에서 피어오르는 김 하나. */
  function stream(x: number, z: number, yTop: number, yBot: number, r: number) {
    const h = yTop - yBot, m = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.8, r, h, 10, 1, true), streamMat);
    m.position.set(x, yBot + h / 2, z); world.add(m); streams.push(m);
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: steamTex(), transparent: true, depthWrite: false, opacity: 0 }));
    sp.userData = { x, z, off: rnd(x * 7 + z) * 7, life: 3.5, k: 0.7 }; world.add(sp); Q.steam.push(sp);
  }
  L.pools.filter((P) => P.fall).forEach((P) => {
    const a = P.gaps[0]! + Math.PI, ux = Math.cos(a), uz = Math.sin(a), px = -uz, pz = ux;
    const outer = P.rAt(a) + 1.7, inner = P.rAt(a) - 0.7, y = 2.3;
    const len = outer - inner, cx = P.x + ux * (outer + inner) / 2, cz = P.z + uz * (outer + inner) / 2;
    const trough = new THREE.Group(); trough.position.set(cx, y, cz); trough.rotation.y = -a; scenery.add(trough);
    const base = mk(new THREE.BoxGeometry(len, 0.08, 0.36), logWood); trough.add(base);
    [-1, 1].forEach((sd) => { const side = mk(new THREE.BoxGeometry(len, 0.2, 0.06), logWood); side.position.set(0, 0.12, sd * 0.16); trough.add(side); });
    const snowT = mk(new THREE.BoxGeometry(len * 0.7, 0.12, 0.42), snowMat); snowT.position.set(-len * 0.12, 0.27, 0); trough.add(snowT);
    [outer - 0.3, outer - 1.5].forEach((d) => {
      [-1, 1].forEach((sd) => {
        const post = mk(new THREE.CylinderGeometry(0.08, 0.1, y, 8), logWood);
        const x = P.x + ux * d + px * 0.3 * sd, z = P.z + uz * d + pz * 0.3 * sd; post.position.set(x, y / 2, z); scenery.add(post); L.obstacles.push({ x, z, r: 0.2 });
      });
    });
    const brace = mk(new THREE.BoxGeometry(0.08, 0.08, 0.7), logWood); brace.position.set(P.x + ux * (outer - 0.3), y - 0.5, P.z + uz * (outer - 0.3)); brace.rotation.y = -a; scenery.add(brace);
    stream(P.x + ux * (inner + 0.05), P.z + uz * (inner + 0.05), y, WATER_Y, 0.09);
  });
  function kakei(P: Pool, a: number) {
    const R = P.rAt(a), x0 = P.x + Math.cos(a) * (R + 1.0), z0 = P.z + Math.sin(a) * (R + 1.0);
    [-1, 1].forEach((sd) => {
      const leg = mk(new THREE.CylinderGeometry(0.04, 0.04, 1.3, 6), bambooPipeMat);
      leg.position.set(x0 - Math.sin(a) * 0.12 * sd, 0.6, z0 + Math.cos(a) * 0.12 * sd); leg.rotation.set(Math.cos(a) * 0.35 * sd, 0, Math.sin(a) * 0.35 * sd); scenery.add(leg);
    });
    const pipe = mk(new THREE.CylinderGeometry(0.05, 0.05, 1.6, 8), bambooPipeMat);
    const mx = P.x + Math.cos(a) * (R + 0.35), mz = P.z + Math.sin(a) * (R + 0.35);
    pipe.position.set(mx, 1.05, mz); pipe.rotation.z = Math.PI / 2; pipe.rotation.y = -a; pipe.rotateZ(-0.12); scenery.add(pipe);
    stream(P.x + Math.cos(a) * (R - 0.45), P.z + Math.sin(a) * (R - 0.45), 0.95, WATER_Y, 0.035);
    L.obstacles.push({ x: x0, z: z0, r: 0.35 });
  }
  kakei(L.big, FRONT + 1.55); kakei(L.pools[6]!, L.pools[6]!.gaps[0]! + 2.4);
  {
    const P = L.pools[7]!, a = P.gaps[0]! + Math.PI * 0.75, R = P.r + 0.9, x = P.x + Math.cos(a) * R, z = P.z + Math.sin(a) * R;
    const post = mk(new THREE.BoxGeometry(0.22, 1.6, 0.22), cedarDark); post.position.set(x, 0.8, z); scenery.add(post);
    const arm = new THREE.Group(); arm.position.set(x, 1.45, z); arm.rotation.y = -a + Math.PI; scenery.add(arm);
    const box = mk(new THREE.BoxGeometry(1.1, 0.18, 0.2), cedarDark); box.position.x = 0.5; arm.add(box);
    const lip = mk(new THREE.BoxGeometry(0.2, 0.08, 0.16), cedarDark); lip.position.set(1.08, -0.1, 0); lip.rotation.z = -0.5; arm.add(lip);
    const tip = new THREE.Vector3(1.12, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), -a + Math.PI);
    stream(x + tip.x, z + tip.z, 1.32, WATER_Y, 0.05); L.obstacles.push({ x, z, r: 0.3 });
  }
  return streams;
}
